import { message } from 'antd';
import axios from 'axios';
import type { Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from 'react-query';

export interface MenuPriceRow {
  /** 易碩 goods.gid */
  productId: number;
  /** 易碩 goods.bn，＝ simply-carbs Product.idNumber */
  idNumber: string | null;
  esitProductName: string;
  esitProductEnName: string | null;
  esitProviderName: string | null;
  locations: Array<{ id: number; name: string | null }>;
  firstDate: string;
  lastDate: string;
  dateCount: number;
  productExists: boolean;
  simplyProductName: string | null;
  simplyProviderName: string | null;
  commercialTerms: string | null;
  inStorePrice: number | null;
  cost: number | null;
  salePrice: number | null;
  state: string | null;
  /** 缺店內價是否會讓對帳跑不出來 */
  blocksStatement: boolean;
}

export interface MenuPriceLocation {
  location_id: number;
  location_name: string;
}

export interface ImportMenuPriceReport {
  total: number;
  updated: number;
  skipped: number;
  unchanged: number;
  notFound: string[];
  invalid: Array<{ row: number; idNumber: string; reason: string }>;
  dryRun: boolean;
}

export interface FindMenuPricesParams {
  skip: number;
  take: number;
  startDate?: Dayjs;
  endDate?: Dayjs;
  locationIds?: number[];
  productName?: string;
  providerName?: string;
  missingPriceOnly?: boolean;
}

const toParams = (params: FindMenuPricesParams) => ({
  startDate: params.startDate?.format('YYYY-MM-DD'),
  endDate: params.endDate?.format('YYYY-MM-DD'),
  locationIds: params.locationIds?.length ? params.locationIds : undefined,
  productName: params.productName || undefined,
  providerName: params.providerName || undefined,
  missingPriceOnly: params.missingPriceOnly ? true : undefined,
});

export function useMenuPrices(params: FindMenuPricesParams) {
  return useQuery(
    ['external-menu-prices', params],
    async () => {
      const response = await axios.get<{
        items: MenuPriceRow[];
        total: number;
        /** 易碩已排定菜單的日期範圍；只在查無資料時回傳 */
        menuDateRange?: { first: string; last: string } | null;
      }>('/external-menu-prices', {
        params: { skip: params.skip, take: params.take, ...toParams(params) },
      });
      return response.data;
    },
    { enabled: !!params.startDate && !!params.endDate },
  );
}

export function useMenuPriceLocations() {
  return useQuery(['external-menu-prices', 'locations'], async () => {
    const response = await axios.get<MenuPriceLocation[]>(
      '/external-menu-prices/locations',
    );
    return response.data;
  });
}

export function useUpdateMenuPrice() {
  const queryClient = useQueryClient();
  return useMutation(
    ['external-menu-prices', 'update'],
    (data: { idNumber: string; inStorePrice: number }) =>
      axios.patch('/external-menu-prices', data),
    {
      onSuccess: () => {
        message.success('店內價已更新');
        queryClient.invalidateQueries(['external-menu-prices']);
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.message ?? '店內價更新失敗');
      },
    },
  );
}

export function useCreateMenuProduct() {
  const queryClient = useQueryClient();
  return useMutation(
    ['external-menu-prices', 'create-product'],
    async (productId: number) => {
      const response = await axios.post<{
        idNumber: string;
        name: string;
        providerName: string;
        created: boolean;
      }>('/external-menu-prices/create-product', { productId });
      return response.data;
    },
    {
      onSuccess: (data) => {
        message.success(
          data.created
            ? `已建立商品「${data.name}」，可以填店內價了`
            : `商品「${data.name}」已存在`,
        );
        queryClient.invalidateQueries(['external-menu-prices']);
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.message ?? '建立商品失敗');
      },
    },
  );
}

export function useExportMenuPrices() {
  return useMutation(
    ['external-menu-prices', 'export'],
    async (params: Omit<FindMenuPricesParams, 'skip' | 'take'>) => {
      const response = await axios.get('/external-menu-prices/export', {
        params: toParams(params as FindMenuPricesParams),
        responseType: 'arraybuffer',
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Amazon菜單價格.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      },
      onError: () => {
        message.error('匯出失敗');
      },
    },
  );
}
